import {
  TNumberInput,
  NumberInput as AppNumberInput,
  TNumberInputChangeEvent,
  ApiaUtil,
} from '@apia/components';
import { formatMessage } from '@apia/util';
import React from 'react';
import { ThemeUICSSObject } from '@apia/theme';
import { FormContext } from '../form/FormContext';
import { useFieldStatesClassNames } from '../hooks';
import { useField } from '../hooks/useField';
import { useValidationClass } from '../hooks/useValidationClass';
import { validationsStore } from '../validationStore';
import { FieldWrapper } from './FieldWrapper';
import { TDefaultFieldType } from './types';

declare global {
  interface Window {
    GNR_NUMERIC: string;
  }
}

type TInput = TNumberInput & TDefaultFieldType;

export const NumberInputf = React.memo(
  React.forwardRef<HTMLInputElement, TInput>(
    (
      {
        avoidSemicolon,
        className,
        label,
        onChange: outerOnChange,
        /**
         * useField
         */
        avoidRegisterIfExists,
        formName: propsFormName,
        initialValue,
        name,
        removeOnUnmount,
        submitValueParser,
        validationClass,
        validationFunction,
        validationRules,
        value,
        ...props
      },
      ref,
    ) => {
      const { name: contextFormName } = React.useContext(FormContext);
      const formName = propsFormName ?? contextFormName;

      const { actualValidationFunction, actualValidationRules } =
        useValidationClass({
          formName,
          name,
          validationClass,
          validationFunction,
          validationRules,
        });

      const { onChange, value: innerValue } = useField<string, number>({
        formName,
        name,
        initialValue:
          initialValue ??
          (value as string) ??
          validationsStore.getFieldStore(formName, name)?.value ??
          '',
        removeOnUnmount,
        submitValueParser: React.useCallback(
          (current: string) =>
            ApiaUtil.instance.parsers.jsNumber(current ?? ''),
          [],
        ),
        validationValueParser: React.useCallback(
          (current: string) =>
            ApiaUtil.instance.parsers.jsNumber(current ?? ''),
          [],
        ),
        validationFunction: actualValidationFunction,
        validationRules: actualValidationRules,
      });

      const handleOnChange = React.useCallback(
        (ev: TNumberInputChangeEvent) => {
          if (ev.error) {
            outerOnChange?.({ ...ev, value: '' });
            onChange('');
            validationsStore.updateField(formName, {
              name,
              error: formatMessage(window.GNR_NUMERIC, { TOK: '' }),
            });
          } else {
            onChange(ev.value);
            outerOnChange?.(ev);
            validationsStore.updateField(formName, { name, error: null });
          }
        },
        [formName, name, onChange, outerOnChange],
      );
      const stateClassName = useFieldStatesClassNames(name);

      return (
        <FieldWrapper
          name={name}
          label={label}
          sx={React.useMemo(
            () =>
              ({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'start',
              }) as ThemeUICSSObject,
            [],
          )}
          className={`input ${className ?? ''}`}
          avoidSemicolon={avoidSemicolon}
          title={props.title}
        >
          <AppNumberInput
            ref={ref}
            {...props}
            name={name}
            value={innerValue}
            onChange={handleOnChange}
            className={stateClassName}
          />
        </FieldWrapper>
      );
    },
  ),
);

NumberInputf.displayName = 'NumberInputf';
